import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Mail, MessageSquare, X, Trash2, Inbox, Phone } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { api } from '../../lib/api';

// Demo-only Message Center: shows every notification the system sent
// (real emails, redirected to the catch inbox) and every text it WOULD
// send (SMS is simulated in the demo). Floating envelope button sits
// above the mascot guide; the panel polls while open so messages appear
// seconds after you trigger them (book an appointment, invite a client…).

export default function DemoOutbox() {
  const isDemo = process.env.REACT_APP_DEMO === 'true';
  const [hasSession, setHasSession] = useState(false);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');   // all | email | sms
  const [expanded, setExpanded] = useState(null);
  const [badge, setBadge] = useState(0);
  const lastSeenRef = useRef(Number(sessionStorage.getItem('demo-outbox-seen') || 0));

  useEffect(() => {
    if (!isDemo) return;
    supabase.auth.getSession().then(({ data }) => setHasSession(!!data?.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setHasSession(!!s));
    return () => sub.subscription.unsubscribe();
  }, [isDemo]);

  const load = useCallback(() => {
    api.demoOutbox()
      .then(rows => {
        setItems(rows);
        const newest = rows.length ? new Date(rows[0].created_at).getTime() : 0;
        setBadge(rows.filter(r => new Date(r.created_at).getTime() > lastSeenRef.current).length);
        if (open && newest) {
          lastSeenRef.current = newest;
          sessionStorage.setItem('demo-outbox-seen', String(newest));
          setBadge(0);
        }
      })
      .catch(() => {});
  }, [open]);

  // Poll: every 6s while open, every 30s while closed (to keep the badge fresh)
  useEffect(() => {
    if (!isDemo || !hasSession) return;
    load();
    const t = setInterval(load, open ? 6000 : 30000);
    return () => clearInterval(t);
  }, [isDemo, hasSession, open, load]);

  if (!isDemo || !hasSession) return null;

  const shown = items.filter(i => filter === 'all' || i.channel === filter);

  const clear = async () => {
    try { await api.demoOutboxClear(); setItems([]); setBadge(0); } catch { /* noop */ }
  };

  const timeOf = (iso) => new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  return (
    <>
      {/* Floating envelope button, stacked above the mascot */}
      <button
        onClick={() => setOpen(o => !o)}
        aria-label="Demo message center"
        style={{
          position: 'fixed', zIndex: 2147482001,
          ...(window.innerWidth < 640 ? { right: 14, bottom: 108 } : { right: 22, bottom: 138 }),
          width: 48, height: 48, borderRadius: 24, border: 'none', cursor: 'pointer',
          background: 'linear-gradient(135deg, #0DA7CA, #22346A)', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 12px 24px -10px rgba(16,23,51,0.5)',
        }}
      >
        <Inbox size={20} />
        {badge > 0 && (
          <span style={{
            position: 'absolute', top: -4, right: -4, minWidth: 18, height: 18,
            borderRadius: 9, background: '#f43f5e', color: '#fff',
            fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center',
            justifyContent: 'center', padding: '0 4px',
          }}>{badge}</span>
        )}
      </button>

      {open && (
        <div style={{
          position: 'fixed', top: 0, right: 0, bottom: 30, width: 'min(400px, 92vw)',
          zIndex: 2147482002, background: '#fff', color: '#101733',
          boxShadow: '-18px 0 50px -20px rgba(16,23,51,0.45)',
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: '16px 18px 12px', borderBottom: '1px solid #eef0f4' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#22346A' }}>Message Center</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={clear} title="Clear all" style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af', padding: 6 }}>
                  <Trash2 size={15} />
                </button>
                <button onClick={() => setOpen(false)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af', padding: 6 }}>
                  <X size={16} />
                </button>
              </div>
            </div>
            <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 2 }}>
              Every email and text the system sends, as clients receive them. Texts are simulated in the demo.
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
              {[['all', 'All'], ['email', 'Email'], ['sms', 'Text'], ['call', 'Call']].map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setFilter(k)}
                  style={{
                    border: 'none', cursor: 'pointer', borderRadius: 999, padding: '5px 13px',
                    fontSize: 12, fontWeight: 700,
                    background: filter === k ? '#22346A' : '#f1f3f7',
                    color: filter === k ? '#fff' : '#4b5563',
                  }}
                >{label}</button>
              ))}
            </div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px 20px' }}>
            {!shown.length && (
              <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, padding: '60px 20px' }}>
                Nothing yet. Book an appointment, invite a client or share a document — notifications appear here in seconds.
              </div>
            )}
            {shown.map(m => m.channel === 'call' ? (
              /* Automated phone call — what the office hears */
              <div key={m.id} style={{ margin: '14px 0' }}>
                <div style={{ fontSize: 10.5, color: '#9ca3af', margin: '0 0 4px 6px' }}>
                  <Phone size={10} style={{ verticalAlign: '-1px', marginRight: 4 }} />
                  Call to {m.to_name || m.to_address} ({m.to_address}) · {timeOf(m.created_at)}
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, #22346A, #0DA7CA)', color: '#fff',
                  borderRadius: 14, padding: '10px 14px', fontSize: 13, lineHeight: 1.45,
                  boxShadow: '0 4px 10px -6px rgba(34,52,106,0.6)',
                }}>
                  <div style={{ fontSize: 10.5, opacity: 0.8, marginBottom: 4, fontWeight: 700, letterSpacing: 0.4 }}>VOICE · spoken on pickup</div>
                  “{m.body}”
                </div>
              </div>
            ) : m.channel === 'sms' ? (
              /* Text message — phone bubble */
              <div key={m.id} style={{ margin: '14px 0' }}>
                <div style={{ fontSize: 10.5, color: '#9ca3af', margin: '0 0 4px 6px' }}>
                  <MessageSquare size={10} style={{ verticalAlign: '-1px', marginRight: 4 }} />
                  Text to {m.to_name || m.to_address} ({m.to_address}) · {timeOf(m.created_at)}
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, #34c759, #2eb350)', color: '#fff',
                  borderRadius: '18px 18px 4px 18px', padding: '10px 14px',
                  fontSize: 13, lineHeight: 1.45, maxWidth: 300, marginLeft: 'auto',
                  boxShadow: '0 4px 10px -6px rgba(46,179,80,0.6)',
                }}>
                  {m.body}
                </div>
              </div>
            ) : (
              /* Email — inbox card */
              <div
                key={m.id}
                onClick={() => setExpanded(expanded === m.id ? null : m.id)}
                style={{
                  margin: '10px 0', border: '1px solid #eef0f4', borderRadius: 12,
                  padding: '11px 13px', cursor: 'pointer', background: '#fbfcfe',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{
                    width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                    background: 'rgba(13,167,202,0.12)', color: '#0DA7CA',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Mail size={14} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {m.subject || '(no subject)'}
                    </div>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>
                      to {m.to_name || m.to_address} · {timeOf(m.created_at)}
                    </div>
                  </div>
                </div>
                {expanded === m.id && m.body && (
                  <div
                    style={{
                      marginTop: 10, borderTop: '1px solid #eef0f4', paddingTop: 10,
                      fontSize: 13, overflowX: 'auto', background: '#fff',
                      borderRadius: 8, padding: 12,
                    }}
                    // Our own server-rendered templates — trusted content
                    dangerouslySetInnerHTML={{ __html: m.body }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
